import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroTabPanel, WalteroButton } from './waltero';
import { hasOrganizationAccess } from '../utils/auth';
import { ADMIN_ORGANIZATION_ID } from '../config/api';
import { fetchOrganizationConfiguration, fetchDevices } from '../services/apiService';
import './PerformanceTab.css';

// Import new modular components
import { PerformanceDataProvider, usePerformanceData } from '../context/PerformanceDataContext';
import { SharedPerformanceCacheProvider, useSharedPerformanceCache } from '../context/SharedPerformanceCache';
import PerformanceSummaryCards from './performance/PerformanceSummaryCards';
import PerformanceTabNavigation from './performance/PerformanceTabNavigation';
import SimpleConnectivityTable from './performance/SimpleConnectivityTable';
import SimplePayloadTable from './performance/SimplePayloadTable';
import HourlyConnectivityHistogram from './performance/HourlyConnectivityHistogram';
import SimpleBatteryTable from './performance/SimpleBatteryTable';
import SimpleTemperatureTable from './performance/SimpleTemperatureTable';
import SimpleRSSITable from './performance/SimpleRSSITable';
import SimpleBootReasonTable from './performance/SimpleBootReasonTable';
import SimpleSuccessRateTable from './performance/SimpleSuccessRateTable';
import SimpleLogsTable from './performance/SimpleLogsTable';
import SuccessRateDataTable from './performance/SuccessRateDataTable';
import ShadowConfigurationTable from './diagnostic/ShadowConfigurationTable';

import BatteryAnalytics from './performance/BatteryAnalytics';
import DiagnosticConfigurationAnalysis from './performance/DiagnosticConfigurationAnalysis';
import PerformanceDataOverlays from './performance/PerformanceDataOverlays';
import PerformanceImageModal from './performance/PerformanceImageModal';
import HourlyConnectivityChart from './performance/HourlyConnectivityChart';
import SuccessRateAnalytics from './performance/SuccessRateAnalytics';

// Internal component that uses the context
const PerformanceTabContent = ({ selectedOrgId, startDate, endDate }) => {
  // Use SharedPerformanceCache for summary stats
  const { getSummaryStats, isSummaryReady } = useSharedPerformanceCache();
  
  const { 
    loadPerformanceData, 
    isLoading, 
    error, 
    performanceData,
    activeTab,
    // All hover state from context
    hoverData,
    hoverPosition,
    shadowUpdateHoverData,
    shadowUpdateHoverPosition,
    bootReasonHoverData,
    bootReasonHoverPosition,
    payloadHoverData,
    payloadHoverPosition,
    // Image modal state from context
    imageModalOpen,
    selectedImage,
    modalPictures,
    currentImageIndex,
    // Actions from context
    handleImageModalOpen,
    handleImageModalClose,
    handleNextImage,
    handlePrevImage,
    setHover
  } = usePerformanceData();

  console.log('🎯 PerformanceTab rendered with props:', { selectedOrgId, startDate, endDate });
  console.log('🎯 Current activeTab:', activeTab);



  // Shadow configuration state
  const [shadowConfiguration, setShadowConfiguration] = useState(null);
  const [shadowConfigDevices, setShadowConfigDevices] = useState([]);
  const [shadowConfigLoading, setShadowConfigLoading] = useState(false);
  const [shadowConfigError, setShadowConfigError] = useState(null);

  // Calculate fleet stats from SharedPerformanceCache summary data
  const sharedSummaryStats = getSummaryStats();

  // Use SharedPerformanceCache summary stats as primary source
  const fleetStats = sharedSummaryStats ? {
    totalDevices: sharedSummaryStats.totalDevices || 0,
    activeDevices: sharedSummaryStats.activeDevices || 0,
    devicesWithData: sharedSummaryStats.devicesWithData || 0,
    averageSuccessRate: sharedSummaryStats.averageSuccessRate || 0,
    averageConnectionPercentage: sharedSummaryStats.averageConnectionPercentage || 0,
    totalTransmissions: sharedSummaryStats.totalTransmissions || 0,
    averageRSSI: sharedSummaryStats.averageRSSI || 0,
    batteryHealthPercentage: sharedSummaryStats.batteryHealthPercentage || null,
    averageBatteryVoltage: sharedSummaryStats.averageBatteryVoltage || null,
    // Distribution data for histograms
    connectionPercentages: sharedSummaryStats.connectionPercentages || [],
    rssiValues: sharedSummaryStats.rssiValues || [],
    batteryVoltages: sharedSummaryStats.batteryVoltages || [],
    successRates: sharedSummaryStats.successRates || [],
    // Loading state for summary cards
    isLoading: !isSummaryReady(hasOrganizationAccess(ADMIN_ORGANIZATION_ID))
  } : null;

  // Load shadow configuration when diagnostic tab is accessed
  useEffect(() => {
    const loadShadowConfiguration = async () => {
      if (!selectedOrgId || activeTab !== 'diagnostic' || shadowConfiguration) return;

      setShadowConfigLoading(true);
      setShadowConfigError(null);

      try {
        console.log('🔧 Loading shadow configuration for organization:', selectedOrgId);

        // Fetch devices and configuration in parallel
        const [devicesResponse, configResponse] = await Promise.all([
          fetchDevices(selectedOrgId, { limit: 1000 }),
          fetchOrganizationConfiguration(selectedOrgId)
        ]);

        console.log('✅ Shadow configuration loaded:', configResponse);
        setShadowConfiguration(configResponse.configurations);
        setShadowConfigDevices(devicesResponse.data || []);
      } catch (error) {
        console.error('❌ Failed to load shadow configuration:', error);
        setShadowConfigError('Failed to load device configuration data');
      } finally {
        setShadowConfigLoading(false);
      }
    };

    loadShadowConfiguration();
  }, [selectedOrgId, activeTab, shadowConfiguration]);

  // DISABLED: Old PerformanceDataContext auto-load - using SharedPerformanceCache only
  // useEffect(() => {
  //   console.log('🔄 Auto-load useEffect triggered with:', { selectedOrgId, startDate, endDate });
  //   if (selectedOrgId && selectedOrgId !== 'undefined' && selectedOrgId !== undefined && startDate && endDate) {
  //     console.log('✅ Auto-load - calling loadPerformanceData');
  //     loadPerformanceData(selectedOrgId, startDate, endDate);
  //   } else {
  //     console.log('❌ Auto-load - missing required props');
  //   }
  // }, [selectedOrgId, startDate, endDate, loadPerformanceData]);

  // Remove blocking loading screen - show page immediately

  if (error) {
    console.log('❌ Performance tab has error:', error);
    return (
      <Box sx={{ p: 3, bgcolor: '#fdf3eb', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center', borderColor: '#c35d2c' }}>
          <WalteroTypography 
            variant="h6" 
            sx={{ color: '#c35d2c' }}
            role="alert"
            aria-live="assertive"
          >
            Error: {error}
          </WalteroTypography>
        </WalteroCard>
      </Box>
    );
  }

  // Early return if no organization is selected
  if (!selectedOrgId || selectedOrgId === 'undefined' || selectedOrgId === undefined || selectedOrgId === '') {
    return (
      <Box sx={{ p: 3, bgcolor: '#fdf3eb', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="secondary">
            Please select an organization to view performance data
          </WalteroTypography>
        </WalteroCard>
      </Box>
    );
  }

  // Early return if no dates are provided
  if (!startDate || !endDate) {
    return (
      <Box sx={{ p: 3, bgcolor: '#fdf3eb', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="secondary">
            Please select start and end dates to view performance data
          </WalteroTypography>
        </WalteroCard>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#fdf3eb', minHeight: '100vh' }}>

      {/* Summary Cards */}
      <PerformanceSummaryCards fleetStats={fleetStats} performanceData={performanceData} />

      {/* Tab Navigation */}
      <PerformanceTabNavigation />

      {/* Tab Content */}
      <div className="performance-content">
        <WalteroTabPanel value={activeTab} index="connectivity">
          {/* Simple Device Connectivity Daily Table */}
          <SimpleConnectivityTable 
            selectedOrgId={selectedOrgId} 
            startDate={startDate} 
            endDate={endDate}
          />
          
          {/* Simple Device Payload Daily Table */}
          <SimplePayloadTable 
            selectedOrgId={selectedOrgId} 
            startDate={startDate} 
            endDate={endDate}
          />
          
          {/* Hourly Connectivity Distribution Histogram */}
          <HourlyConnectivityHistogram selectedOrgId={selectedOrgId} startDate={startDate} endDate={endDate} />
        </WalteroTabPanel>
        
        <WalteroTabPanel value={activeTab} index="battery">
          {/* Battery Analytics with Box Plot */}
          <BatteryAnalytics />
          
          {/* Simple Battery Daily Table */}
          <SimpleBatteryTable selectedOrgId={selectedOrgId} startDate={startDate} endDate={endDate} />
          
          {/* Simple Temperature Daily Table */}
          <SimpleTemperatureTable selectedOrgId={selectedOrgId} startDate={startDate} endDate={endDate} />
          
          {/* Simple RSSI Daily Table */}
          <SimpleRSSITable selectedOrgId={selectedOrgId} startDate={startDate} endDate={endDate} />
        </WalteroTabPanel>
        
        {hasOrganizationAccess(ADMIN_ORGANIZATION_ID) && (
          <>
            <WalteroTabPanel value={activeTab} index="success">
              {console.log('🎯 SUCCESS TAB RENDERING - performanceData:', performanceData?.length, 'sharedSummary:', sharedSummaryStats)}
              
              {/* Simple Success Rate Daily Table with Optimized Endpoint */}
              <SimpleSuccessRateTable selectedOrgId={selectedOrgId} startDate={startDate} endDate={endDate} />
            </WalteroTabPanel>
            
            <WalteroTabPanel value={activeTab} index="diagnostic">
              {/* Simple Boot Reason Table */}
              <SimpleBootReasonTable selectedOrgId={selectedOrgId} startDate={startDate} endDate={endDate} />

              {/* Shadow Configuration Table */}
              <Box mt={4}>
                {shadowConfigError ? (
                  <Alert severity="error">{shadowConfigError}</Alert>
                ) : (
                  <ShadowConfigurationTable
                    configurations={shadowConfiguration}
                    devices={shadowConfigDevices}
                    loading={shadowConfigLoading}
                  />
                )}
              </Box>
            </WalteroTabPanel>

            <WalteroTabPanel value={activeTab} index="logs">
              {/* Simple Logs Availability Table */}
              <SimpleLogsTable selectedOrgId={selectedOrgId} startDate={startDate} endDate={endDate} />
            </WalteroTabPanel>
          </>
        )}
        
        {/* Sensor Issues Tab - kept as placeholder */}
        <WalteroTabPanel value={activeTab} index="sensorissues">
          <WalteroCard sx={{ p: 4, textAlign: 'center' }}>
            <WalteroTypography variant="h6" color="secondary">
              Sensor Issues configuration tracking - To be implemented
            </WalteroTypography>
          </WalteroCard>
        </WalteroTabPanel>
      </div>

      {/* Performance Data Overlays - All 4 hover types */}
      <PerformanceDataOverlays 
        hoverData={hoverData}
        hoverPosition={hoverPosition}
        shadowUpdateHoverData={shadowUpdateHoverData}
        shadowUpdateHoverPosition={shadowUpdateHoverPosition}
        bootReasonHoverData={bootReasonHoverData}
        bootReasonHoverPosition={bootReasonHoverPosition}
        payloadHoverData={payloadHoverData}
        payloadHoverPosition={payloadHoverPosition}
      />

      {/* Performance Image Modal with Navigation */}
      <PerformanceImageModal 
        isOpen={imageModalOpen}
        selectedImage={selectedImage}
        modalPictures={modalPictures}
        currentImageIndex={currentImageIndex}
        onClose={handleImageModalClose}
        onNextImage={handleNextImage}
        onPrevImage={handlePrevImage}
      />

    </Box>
  );
};

// Main wrapper component with provider
const PerformanceTab = ({ selectedOrgId, startDate, endDate }) => {
  return (
    <SharedPerformanceCacheProvider>
      <PerformanceDataProvider>
        <PerformanceTabContent 
          selectedOrgId={selectedOrgId}
          startDate={startDate}
          endDate={endDate}
        />
      </PerformanceDataProvider>
    </SharedPerformanceCacheProvider>
  );
};

export default PerformanceTab;